import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  costFormater,
  Preloader,
  Responsive,
  Text,
  useResponsive,
  WarningIcon,
} from '@common-fe/common-fe';

import { Modal } from '@/components';

import { AllocationPercentsMap, FundPayload } from '../../SelfDirected.types';

import ConfirmModalItem from './ConfirmModalItem';
import ConfirmModalListHeader from './ConfirmModalListHeader';
interface Props {
  list: FundPayload[];
  isLoading?: boolean;
  investedAmount: number;
  isSwitchMode?: boolean;
  visible: boolean;
  percentsMap: AllocationPercentsMap;
  onSetVisible: (value: boolean) => void;
  onSubmit: () => void;
  isInvestMoreMode?: boolean;
}
const ConfirmModal: React.FC<Props> = ({
  list,
  isSwitchMode,
  visible,
  onSetVisible,
  isLoading,
  investedAmount,
  onSubmit,
  percentsMap,
  isInvestMoreMode,
}) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const handleClose = useCallback(() => {
    if (isLoading) {
      return;
    }
    onSetVisible(false);
  }, [isLoading, onSetVisible]);

  return (
    <Modal testId="confirm_modal" visible={visible} onSetVisible={onSetVisible}>
      <Box direction="column">
        <Text size="2xl" textAlign="center" weight={700} data-testid="confirm_modal_title">
          {!isInvestMoreMode ? t('Confirm') : t('Confirm Selections')}
        </Text>
      </Box>
      {isSwitchMode ? (
        <>
          <Text textAlign="center" size="medium" data-testid="confirm_modal_switch-title">
            {t('Updating your investment model will take 3 - 4 business days to complete.')}
          </Text>
          <Text textAlign="center" size="medium">
            {t(
              'During that time you will not be able to make changes to your investment portfolio. This includes additional buys or sells.'
            )}
          </Text>
        </>
      ) : null}

      <Box
        direction="column"
        background="module"
        margin={{ top: 'spacing16' }}
        pad={isMobile ? 'spacing8' : 'spacing24'}
        round="moduleRound"
      >
        <Responsive sizes={[Responsive.Desktop, Responsive.Tablet]}>
          <ConfirmModalListHeader />
        </Responsive>

        <Box direction="column" gap="spacing8">
          {list.map((item) => (
            <ConfirmModalItem
              testId={`confirm-modal_item_${item.id}`}
              percents={percentsMap[item.id] || 0}
              investedAmount={investedAmount}
              key={item.id}
              {...item}
            />
          ))}
        </Box>
        <Box
          direction="row"
          pad={isMobile ? 'spacing12' : { vertical: '6px', horizontal: 'spacing24' }}
          background="border1"
          margin={{ top: 'xs' }}
          round="container2Round"
          justify="between"
        >
          <Text size={isMobile ? 'large' : 'medium'} weight={700}>
            {t('Total')}
          </Text>
          <Box direction="row" justify={isMobile ? 'end' : 'start'} width="175px">
            <Box>
              <Text
                size={isMobile ? 'large' : 'medium'}
                textAlign="end"
                data-testid="confirm_modal_invested-amount"
                weight={700}
              >
                {costFormater(investedAmount, {
                  isPrecision: true,
                })}
              </Text>
            </Box>
          </Box>
        </Box>
        {!isInvestMoreMode && (
          <Box
            background="canvas"
            data-testid="confirm-modal_invest-more_text"
            border={{ color: 'border2', size: 'small' }}
            round="container1Round"
            pad="s"
            margin={{ top: 'l' }}
            elevation="default"
            style={{ position: 'relative' }}
          >
            <Box
              height="2px"
              round="small"
              background="warning"
              style={{ position: 'absolute', top: '-0.5px', left: '12px', right: '12px' }}
            />

            <Box
              direction="row"
              align="center"
              background="warningContainer"
              border={{ color: 'warning', size: 'small' }}
              round="container2Round"
              pad={{ vertical: 'm', horizontal: 'l' }}
            >
              <Box>
                <WarningIcon />
              </Box>
              <Text margin={{ left: 'xs' }}>
                {t(
                  'Actual share price and number of shares will be calculated at the time the trade is executed.'
                )}
              </Text>
            </Box>
          </Box>
        )}
      </Box>

      <Box
        direction="row"
        justify="end"
        gap="spacing12"
        margin={{ top: isMobile ? 'spacing12' : 'spacing24' }}
      >
        <Box fill={isMobile}>
          <AppButton
            fill={isMobile}
            buttonType="secondary"
            width="140px"
            testId="confirm-modal_back"
            onClick={handleClose}
          >
            {t('Back')}
          </AppButton>
        </Box>
        <Box fill={isMobile}>
          <AppButton
            fill={isMobile}
            testId="confirm-modal_submit"
            width="140px"
            disabled={isLoading}
            onClick={onSubmit}
          >
            {isLoading ? <Preloader color="white" /> : t('Confirm')}
          </AppButton>
        </Box>
      </Box>
    </Modal>
  );
};
export default ConfirmModal;
